import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { logoutUser } from "../../firebase/services";
import { useAuth } from "../../hooks/useAuth";
import { useLanguage } from "../../hooks/useLanguage";
import ChangePasswordModal from "../../components/ChangePasswordModal";
import LangSwitcher, { LangInline } from "../../components/LangSwitcher";

const NavItem = ({ to, icon, label }) => (
  <NavLink to={to} className={({ isActive }) => "nav-item" + (isActive ? " active" : "")}>
    <span style={{ fontSize: 16 }}>{icon}</span>
    <span>{label}</span>
  </NavLink>
);

// Bottom tab item — uses NavLink for active state
const TabItem = ({ to, icon, label, end }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => "bottom-tab-item" + (isActive ? " active" : "")}
  >
    <span className="tab-icon">{icon}</span>
    <span>{label}</span>
  </NavLink>
);

// MORE_ITEMS built dynamically inside component using t()

export default function AdminLayout() {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showChangePw, setShowChangePw] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const moreItems = [
    { to: "/admin/reports",   icon: "📈", label: t("nav.reports") },
    { to: "/admin/promoters", icon: "👥", label: t("nav.promoters") },
    { to: "/admin/targets",   icon: "🎯", label: t("nav.targets") },
    { to: "/admin/skus",      icon: "💻", label: t("nav.skus") },
    { to: "/admin/settings",  icon: "⚙️", label: t("nav.settings") },
    { to: "/admin/notifications",icon:"🔔",label: t("nav.notifications") },
    { to: "/admin/training",  icon: "📚", label: t("nav.training") },
    { to: "/admin/audit",     icon: "📋", label: t("nav.audit") },
  ];

  return (
    <div className="app-shell">
      {/* ── Sidebar (desktop) ── */}
      <aside className="sidebar">
        <div className="sidebar-logo">PromoterHQ <span>{t("nav.administrator")}</span></div>

        <nav style={{ flex: 1, overflowY: "auto" }}>
          <div className="sidebar-section">
            <NavItem to="/admin" icon="▦" label={t("nav.dashboard")} />
          </div>
          <div className="sidebar-section">
            <NavItem to="/admin/promoters" icon="👥" label={t("nav.promoters")} />
            <NavItem to="/admin/sales"     icon="📊" label={t("nav.sales")} />
            <NavItem to="/admin/reports"   icon="📈" label={t("nav.reports")} />
            <NavItem to="/admin/attendance"icon="📅" label={t("nav.attendance")} />
            <NavItem to="/admin/salary"    icon="💰" label={t("nav.salary")} />
          </div>
          <div className="sidebar-section">
            <NavItem to="/admin/targets"      icon="🎯" label={t("nav.targets")} />
            <NavItem to="/admin/skus"         icon="💻" label={t("nav.skus")} />
            <NavItem to="/admin/settings"     icon="⚙️" label={t("nav.settings")} />
            <NavItem to="/admin/notifications"icon="🔔" label={t("nav.notifications")} />
            <NavItem to="/admin/training"     icon="📚" label={t("nav.training")} />
          </div>
          <div className="sidebar-section">
            <NavItem to="/admin/audit" icon="📋" label={t("nav.audit")} />
          </div>
        </nav>

        <div className="sidebar-footer">
          <div style={{ padding: "0 0 8px 0" }}>
            <LangSwitcher />
          </div>
          <div className="profile-menu-wrap">
            <button className="profile-menu-btn" onClick={() => setMenuOpen(o => !o)}>
              <span className="profile-avatar">👤</span>
              <span className="profile-name">{profile?.name}</span>
              <span style={{ marginLeft: "auto", fontSize: 10 }}>{menuOpen ? "▲" : "▼"}</span>
            </button>
            {menuOpen && (
              <div className="profile-dropdown">
                <button className="profile-dropdown-item" onClick={() => { setShowChangePw(true); setMenuOpen(false); }}>
                  🔑 {t("auth.change_pw")}
                </button>
                <div className="profile-dropdown-divider" />
                <button className="profile-dropdown-item profile-dropdown-danger" onClick={logoutUser}>
                  🚪 {t("auth.logout")}
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="main">
        <div className="page-container"><Outlet /></div>
      </main>

      {/* ── Bottom tab bar (mobile) ── */}
      <div className="bottom-tab-bar">
        <div className="bottom-tab-bar-inner">
          <TabItem to="/admin" end icon="▦" label={t("nav.dashboard")} />
          <TabItem to="/admin/sales"     icon="📊" label={t("nav.sales")} />
          <TabItem to="/admin/attendance"icon="📅" label={t("nav.attendance")} />
          <TabItem to="/admin/salary"    icon="💰" label={t("nav.salary_short")} />
          <button className="bottom-tab-item" onClick={() => setShowMore(true)}>
            <span className="tab-icon">☰</span>
            <span>{t("nav.more")}</span>
          </button>
        </div>
      </div>

      {/* ── More sheet (mobile) ── */}
      {showMore && (
        <>
          <div className="more-menu-overlay" onClick={() => setShowMore(false)} />
          <div className="more-menu-sheet">
            <div className="more-menu-handle" />
            {moreItems.map(item => (
              <button key={item.to} className="more-menu-item" onClick={() => { navigate(item.to); setShowMore(false); }}>
                <span className="more-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
            <div style={{ padding: "8px 20px" }}><LangInline /></div>
            <div style={{ height: 1, background: "#f0f0f0", margin: "8px 0" }} />
            <button className="more-menu-item" onClick={() => { setShowChangePw(true); setShowMore(false); }}>
              <span className="more-icon">🔑</span>{t("auth.change_pw")}
            </button>
            <button className="more-menu-item" style={{ color: "#dc2626" }} onClick={logoutUser}>
              <span className="more-icon">🚪</span>{t("auth.logout")}
            </button>
          </div>
        </>
      )}

      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
